import * as React from 'react';

import {Card} from 'shells/card/card';
import {SearchBar} from 'shells/search-bar/search-bar';

import type {FieldCreateInput} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../../../../../core/nutshell-fields/types';
import {translateSchemaEntityTypeToGraphQL, getFieldTypeFromDisplayType} from '../../../../helpers';
import type {
    FormFieldRowForDuplicateHelpers,
    View,
    CustomFieldView,
    DisplayType,
    EntityTypeFromApi,
    SelectFieldInput,
} from '../../../../types';

import {AddCardHeader} from './add-card-header';
import {CreateCustomFieldBlock} from './create-custom-field-block';
import {NutshellFields} from './nutshell-fields';
import {OtherFields} from './other-fields';
import {StandardFields} from './standard-fields';

import styles from './add-card.module.css';

type Props = {
    formId: string;
    fields: FormFieldRowForDuplicateHelpers[];
    formSchema: FormFieldSchema;
    onSelectInputField: (field: FieldCreateInput) => void;
    onClose: () => void;

    fetchFormSchema: () => void;
};

export function AddFieldCard(props: Props) {
    const [nutshellFieldsSearchValue, setNutshellFieldsSearchValue] = React.useState<string>('');
    const [currentView, setCurrentView] = React.useState<View>('standard');
    const [isCreatingField, setIsCreatingField] = React.useState<
        | {
              customFieldType: CustomFieldView;
              name: string;
          }
        | null
        | undefined
    >(undefined);

    React.useEffect(() => {
        if (nutshellFieldsSearchValue) {
            setCurrentView('nutshell-fields');
        } else {
            setCurrentView('standard');
        }
    }, [nutshellFieldsSearchValue]);

    const onSelectInputField = (input: SelectFieldInput) => {
        let fieldName, entityAttributeId;

        if (input.isNewCustomField) {
            // New custom fields do not appear in the form schema field immediately
            entityAttributeId = input.fieldKey;
        } else {
            const schemaField = props.formSchema.properties[input.fieldKey];
            // @ts-expect-error
            fieldName = !schemaField.isCustomField ? schemaField.name : undefined;
            // @ts-expect-error
            entityAttributeId = schemaField.isCustomField ? schemaField.attributeId : undefined;
        }

        props.onSelectInputField({
            type: getFieldTypeFromDisplayType(input.displayType),
            formId: props.formId,
            fieldName,
            entityName: translateSchemaEntityTypeToGraphQL(input.entityType),
            entityAttributeId,
        });
    };

    const hasCompanyNameField = Boolean(
        props.fields.find(
            (field) =>
                // @ts-expect-error
                field.fieldName === 'name' && field.entityType && field.entityType === 'ACCOUNT'
        )
    );

    const shouldShowNutshellSearchBar =
        currentView === 'standard' || currentView === 'nutshell-fields';

    const getMainContent = (view: View) => {
        if (view === 'standard') {
            return (
                <StandardFields
                    hasCompanyNameField={hasCompanyNameField}
                    onSelectInputField={onSelectInputField}
                    setCurrentView={(newView: View) => setCurrentView(newView)}
                />
            );
        }

        if (view === 'nutshell-fields') {
            return (
                <NutshellFields
                    // @ts-expect-error
                    formSchema={props.formSchema}
                    setCurrentView={(newView: View) => setCurrentView(newView)}
                    onSelectInputField={onSelectInputField}
                    fields={props.fields}
                    onClose={props.onClose}
                    nutshellFieldsSearchValue={nutshellFieldsSearchValue}
                    excludeFirstLastNames={true}
                />
            );
        }

        if (isCreatingField) {
            return (
                <CreateCustomFieldBlock
                    customFieldType={isCreatingField.customFieldType}
                    name={isCreatingField.name}
                    onCreateSuccess={(
                        customFieldId: string,
                        displayType: DisplayType,
                        entityType: EntityTypeFromApi
                    ) => {
                        // Trigger a refetch of the form schema
                        props.fetchFormSchema();
                        // Select newly created field
                        onSelectInputField({
                            fieldKey: customFieldId,
                            displayType,
                            entityType,
                            isNewCustomField: true,
                        });
                    }}
                    onClickBack={() => {
                        setIsCreatingField(undefined);
                    }}
                />
            );
        }

        return (
            <OtherFields
                view={view}
                formSchema={props.formSchema}
                onSelectInputField={onSelectInputField}
                fields={props.fields}
                // @ts-expect-error
                onClose={props.onClose}
                setIsCreatingField={(name: string) =>
                    setIsCreatingField({customFieldType: view, name})
                }
            />
        );
    };

    return (
        <div className={styles['card-container--short']}>
            <Card hasBorder={true} hasShadow={true}>
                <AddCardHeader
                    title='Add a field'
                    currentView={currentView}
                    setCurrentView={(view) => {
                        setCurrentView(view);
                        // Need to reset if clicking breadcrumb when creating custom field
                        if (isCreatingField) {
                            setIsCreatingField(undefined);
                        }
                    }}
                    onClose={props.onClose}
                    setNutshellFieldsSearchValue={(value) => setNutshellFieldsSearchValue(value)}
                    isCreatingField={Boolean(isCreatingField)}
                />
                {shouldShowNutshellSearchBar ? (
                    <div className={styles.search}>
                        <SearchBar
                            value={nutshellFieldsSearchValue}
                            onChange={(val) => setNutshellFieldsSearchValue(val)}
                            isClearable={true}
                            hasBorder={true}
                            placeholder='Search Nutshell fields…'
                        />
                    </div>
                ) : undefined}
                {getMainContent(currentView)}
            </Card>
        </div>
    );
}
